import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuthStore } from '../../stores/auth-store';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    emailOrUsername: '',
    password: '',
    rememberMe: true,
  });
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Zustand store
  const { login, isLoading, error, clearError } = useAuthStore();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    clearError();
    
    if (!recaptchaToken) {
      alert('Please complete the reCAPTCHA');
      return;
    }

    try {
      const result = await login(formData.emailOrUsername, formData.password, recaptchaToken);
      
      if (result.success) {
        if (result.requires2FA) {
          // Redirect to 2FA verification page
          navigate('/auth/2fa-verify', {
            state: {
              fromLogin: true,
              userData: result.userData,
              intendedPath: location.state?.intendedPath || '/dashboard',
            },
          });
        } else {
          // Redirect to dashboard on successful login
          navigate('/dashboard');
        }
      }
      // If login fails, error will be displayed via the error state
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-0">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Welcome Back</h1>
        <p className="text-black text-base sm:text-lg">
          Hey! Enter your email or username and password to login to your account.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Email/Username Field */}
        <div>
          <label htmlFor="emailOrUsername" className="block text-sm font-medium text-black mb-2">
            Email or Username
          </label>
          <input
            type="text"
            id="emailOrUsername"
            name="emailOrUsername"
            value={formData.emailOrUsername}
            onChange={handleInputChange}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-sm sm:text-base"
            placeholder="Enter your email or username"
            required
          />
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-black mb-2">
            Password
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-sm sm:text-base"
            placeholder="Enter Password"
            required
          />
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
              className="w-4 h-4 text-secondary-500 bg-white border-gray-300 rounded "
            />
            <span className="ml-2 text-sm text-black">Remember Me</span>
          </label>
          <Link
            to="/auth/forgot-password"
            className="text-sm text-secondary-500 hover:text-secondary-600 font-medium"
          >
            Forget Password?
          </Link>
        </div>

        {/* Sign In Button */}
        <button
          type="submit"
          disabled={isLoading || !recaptchaToken}
          className="w-full bg-secondary-500 text-white py-2.5 sm:py-3 px-4 rounded-lg font-medium hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Signing In...
            </div>
          ) : (
            'Sign In'
          )}
        </button>

        {/* reCAPTCHA */}
        <div className="flex justify-center">
          <ReCAPTCHA
            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
            onChange={handleRecaptchaChange}
            theme="light"
            size="normal"
          />
        </div>
      </form>
    </div>
  );
}
